import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { useSharedWidgetChangeHandler } from '~src/settings/general-settings/utils';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { EmailCustomizationOptions } from './EmailCustomizationOptions';
import { ExternalSMTPConfiguration } from './serverApi/SiteSMTPCredential';

export const REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_FIELD = 'settings.subscriptionAndDDAEmailLinksEnabled';
export const EMAIL_CUSTOMIZATION_ENABLED_FIELD = 'settings.emailCustomizationSettings.isEmailCustomizationEnabled';
export const EMAIL_CUSTOMIZATION_SETTINGS_SECTION_TEST_ID = 'email-customization-settings-section';
export const EMAIL_SERVER_SETTINGS_SUBSECTION_TEST_ID = 'email-server-settings-subsection';
export const EMAIL_CUSTOMIZATION_ENABLE_OPTION_TEST_ID = 'email-customization-on-radio';
export const EMAIL_CUSTOMIZATION_DISABLE_OPTION_TEST_ID = 'email-customization-off-radio';
export const NOTIFICATION_EMAIL_SETTINGS_SUBSECTION_TEST_ID = 'notification-email-settings-subsection';
export const REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_CHECKBOX_TEST_ID = 'remove-subscription-and-dda-email-links-enabled';

export type EmailCustomizationSettingsSectionProps = SiteSettingsForSiteAdminProps<'subscriptionAndDDAEmailLinksEnabled' | 'emailCustomizationSettings'>;

function getButtonsList(props: EmailCustomizationSettingsSectionProps, emailCustomizationSelected?: boolean): RadioButtonWithLabelOption<boolean>[] {
  return [
    {
      value: false,
      label: Messages.emailServerCustomizeOff(),
      testId: EMAIL_CUSTOMIZATION_DISABLE_OPTION_TEST_ID,
    },
    {
      value: true,
      label: <EmailCustomizationOptions {...props}
        useEmailCustomization={emailCustomizationSelected}
      />,
      testId: EMAIL_CUSTOMIZATION_ENABLE_OPTION_TEST_ID,
    }
  ];
}

// W-13682447: TODO disable site settings save if there are no SMTP credentials AND email customization selected
// set formik.setFieldValue(EMAIL_CUSTOMIZATION_ENABLED_FIELD, undefined);
export function EmailCustomizationSettingsSection(props: EmailCustomizationSettingsSectionProps): React.ReactElement {
  const formik = useFormikContext<EmailCustomizationSettingsSectionProps>();
  const emailCustomizationSettings = formik.values.settings.emailCustomizationSettings;

  const [ emailCustomizationRadioSelected, setEmailCustomizationRadioSelected ] = React.useState<boolean>(emailCustomizationSettings?.isEmailCustomizationEnabled);

  const buttons = React.useMemo(() => getButtonsList(props, emailCustomizationRadioSelected),
    [props, emailCustomizationRadioSelected]);

  const index = buttons.findIndex(button => button.value === emailCustomizationSettings?.isEmailCustomizationEnabled);
  const handleChange = useSharedWidgetChangeHandler(EMAIL_CUSTOMIZATION_ENABLED_FIELD);

  if (!FeatureFlags.emailCustomizationEnabled) {
    return null;
  }
  return (
    <>
      <SettingsSection
        title={Messages.emailCustomizationSettingTitle()}
        description={Messages.emailCustomizationSettingDescription()}
        testId={EMAIL_CUSTOMIZATION_SETTINGS_SECTION_TEST_ID}>
        <div style={{paddingBottom: 12}} data-tb-test-id={EMAIL_SERVER_SETTINGS_SUBSECTION_TEST_ID}>
          <RadioButtonWithLabelGroupWidget
            buttonList={buttons}
            aria-label={Messages.emailServerSetting()}
            checkedIndex={index}
            handleChange={(value) => {
              handleChange(value);
              setEmailCustomizationRadioSelected(value);
            }}
          />
        </div>
        {FeatureFlags.removeSubscriptionAndDDAEmailLinksEnabled && <div data-tb-test-id={NOTIFICATION_EMAIL_SETTINGS_SUBSECTION_TEST_ID}>
          <FormikCheckbox
            label={Messages.removeSubscriptionAndDDAEmailLinksDescription()}
            name={REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_FIELD}
            testId={REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_CHECKBOX_TEST_ID} />
        </div>}
      </SettingsSection>
    </>
  );
}

export const EmailCustomizationSettingsSectionSpecs = componentRegistryEntry({
  name: 'EmailCustomizationSettingsSection',
  component: EmailCustomizationSettingsSection,
  onSubmit: (settings) => {
    if(FeatureFlags.emailCustomizationEnabled && !settings.emailCustomizationSettings.isEmailCustomizationEnabled) {
      ExternalSMTPConfiguration.deleteSiteSMTPCredential(SessionSiteSelectors.siteLuid(getAppStore().getState()));
    }
    // if turning on, don't do anything since info has been submitted through credentials form
  },
});
